import { hasAny } from "./access.ts";
import {
  GIVEAWAYS_GRANT,
  GIVEAWAYS_READ,
  GIVEAWAYS_REPEAT_GRANT,
  LEVELING_GRANT,
  LEVELING_MEMBER_CARDS_GRANT,
  LEVELING_PICTURES_READ,
  LEVELING_PICTURES_WRITE,
} from "@colossus/contracts";
import { modulePath } from "@colossus/contracts/module";
import { m } from "./paraglide/messages.js";

export const levelingShown = (grants: readonly string[]): boolean =>
  grants.includes(LEVELING_GRANT);

export const LEVELING_VIEWS = [
  "earning/rules",
  "earning/settings",
  "perks",
  "level-up",
  "cards/rank",
] as const;

export type LevelingView = (typeof LEVELING_VIEWS)[number];

export type LevelingTarget = LevelingView | "earning";

export const EARNING_VIEWS: ReadonlySet<LevelingView> = new Set([
  "earning/rules",
  "earning/settings",
]);

export const LEVELING_VIEW_TITLE = {
  "earning/rules": m.annotations_tab_rules,
  "earning/settings": m.perm_domain_settings,
  perks: m.perks_nav,
  "level-up": m.leveling_page_level_ups,
  "cards/rank": m.leveling_page_rank,
} as const satisfies Record<LevelingView, () => string>;

export const LEVELING_VIEW_SEARCH_WORDS: Readonly<
  Partial<Record<LevelingView, readonly (() => string)[]>>
> = {
  "earning/rules": [m.leveling_earning_rules, m.features_multipliers],
  "earning/settings": [
    m.leveling_climb_title,
    m.leveling_earning_curve,
    m.leveling_curve_title,
    m.leveling_curve_max_level,
    m.leveling_column_prestige,
    m.leveling_issue_filters,
    m.import_levelling_title,
    m.area_tab_switch,
  ],
  perks: [m.perks_achievements_search_words],
};

export const levelingHref = (tenantId: string, view: LevelingTarget): string =>
  modulePath(tenantId, "rewards", view === "earning" ? "earning/rules" : view);

export const MULTIPLIERS_ANCHOR = "multipliers";

export const multipliersHref = (tenantId: string): string =>
  `${levelingHref(tenantId, "earning/rules")}#${MULTIPLIERS_ANCHOR}`;

export const LEVELS_IMPORT_ANCHOR = "import";

export const levelsImportHref = (tenantId: string): string =>
  `${levelingHref(tenantId, "earning/settings")}#${LEVELS_IMPORT_ANCHOR}`;

export const perksHref = (
  tenantId: string,
  more: Readonly<Record<string, string>> = {},
): string => {
  const query = new URLSearchParams(more).toString();
  return `${levelingHref(tenantId, "perks")}${query === "" ? "" : `?${query}`}`;
};

export const levelPerkAnchor = (level: number, roleId: string): string =>
  `level-${level}-${roleId}`;

export const levelPerkHref = (tenantId: string, level: number, roleId: string): string =>
  `${levelingHref(tenantId, "perks")}#${levelPerkAnchor(level, roleId)}`;

export const levelingScope = (tenantId: string): readonly string[] =>
  LEVELING_VIEWS.map((view) => levelingHref(tenantId, view));

const APPROVAL_PAGES = ["pictures", "claims", "roles"] as const;

export type ApprovalPage = (typeof APPROVAL_PAGES)[number];

export const approvalsLanding = (
  grants: readonly string[],
  permissions?: readonly string[],
): ApprovalPage =>
  grants.includes(LEVELING_MEMBER_CARDS_GRANT) &&
  (permissions === undefined ||
    hasAny(permissions, [LEVELING_PICTURES_READ, LEVELING_PICTURES_WRITE]))
    ? "pictures"
    : "claims";

export const approvalsHref = (tenantId: string, page?: ApprovalPage): string =>
  modulePath(tenantId, "workspace", page === undefined ? "requests" : `requests/${page}`);

export const giveawayEntriesHref = (tenantId: string): string =>
  modulePath(tenantId, "workspace", "requests/entries");

export const imagesHref = (tenantId: string, imageId?: string): string =>
  modulePath(tenantId, "rewards", imageId === undefined ? "images" : `images/${imageId}`);

export const giveawaysHeld = (grants: readonly string[]): boolean =>
  grants.includes(GIVEAWAYS_GRANT);

export const repeatHeld = (grants: readonly string[]): boolean =>
  grants.includes(GIVEAWAYS_REPEAT_GRANT);

export const giveawaysShown = (context: {
  readonly grants: readonly string[];
  readonly permissions: readonly string[];
}): boolean => giveawaysHeld(context.grants) && context.permissions.includes(GIVEAWAYS_READ);

export const giveawaysHref = (tenantId: string, giveawayId?: string): string =>
  modulePath(
    tenantId,
    "campaigns",
    giveawayId === undefined ? "giveaways" : `giveaways/${giveawayId}`,
  );

export const giveawayEditHref = (tenantId: string, giveawayId: string): string =>
  `${giveawaysHref(tenantId, giveawayId)}/edit`;

export const newGiveawayHref = (tenantId: string, template?: string): string =>
  template === undefined
    ? giveawaysHref(tenantId, "new")
    : `${giveawaysHref(tenantId, "new")}?${new URLSearchParams({ template }).toString()}`;

export const giveawayDraftHref = (tenantId: string, draftId: string): string =>
  `${giveawaysHref(tenantId, "new")}?${new URLSearchParams({ draft: draftId }).toString()}`;

export const giveawayTemplateHref = (tenantId: string, templateId: string): string =>
  giveawaysHref(tenantId, `templates/${templateId}`);

export const levelingViewAt = (routeId: string | null): LevelingView => {
  const path = routeId?.split("/(leveling)/")[1]?.replace(/^[^/]+\//, "");
  const found = LEVELING_VIEWS.find((view) => path === view);
  return found === undefined ? "earning/rules" : found;
};
